import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Code, Search, Lock, CheckCircle, ArrowRight, MonitorSmartphone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Services = () => {
  const { t } = useLanguage();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero with Parallax */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${scrollY * 0.35}px)`,
            willChange: 'transform'
          }}
        />
        <div className="absolute inset-0 opacity-10 bg-[linear-gradient(rgba(11,112,42,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(11,112,42,0.2)_1px,transparent_1px)] bg-[size:40px_40px]" style={{ transform: `translateY(${scrollY * 0.15}px)`, willChange: 'transform' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#014421]/30 to-black" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">{t.services.title}</h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">{t.services.subtitle}</p>
        </div>
      </section>

      {/* Services Detail with Parallax Backgrounds */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {/* Service 1 - Pentest */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative h-96 rounded-xl overflow-hidden">
                <img src="https://images.unsplash.com/photo-1563206767-5b18f218e8de?w=600" alt="Pentest" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-8 left-8">
                  <div className="w-16 h-16 bg-[#0B702A]/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">{t.services.pentest.title}</h2>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">{t.services.pentest.description}</p>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-4 text-[#0B702A]">{t.services.whatIncludes}</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.pentest.features[0]}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.pentest.features[1]}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.pentest.features[2]}</span>
                    </li>
                  </ul>
                </div>

                <Link to="/pentest" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0B702A] text-white font-semibold rounded-lg hover:bg-[#0C9F39] transition-all duration-300 shadow-lg hover:scale-105">
                  {t.services.learnMore}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>

            {/* Service 2 - Sites & Landing Pages */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center lg:grid-flow-dense">
              <div className="lg:col-start-2 relative h-96 rounded-xl overflow-hidden">
                <img src="https://images.unsplash.com/photo-1525182008055-f88b95ff7980?w=600" alt="Sites e Landing Pages" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-8 left-8">
                  <div className="w-16 h-16 bg-[#0B702A]/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
                    <MonitorSmartphone className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>

              <div className="lg:col-start-1 lg:row-start-1">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">{t.services.websites.title}</h2>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">{t.services.websites.description}</p>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-4 text-[#0B702A]">{t.services.whatIncludes}</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.websites.features[0]}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.websites.features[1]}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.websites.features[2]}</span>
                    </li>
                  </ul>
                </div>

                <a
                  href="https://sites.softwaresecure.com.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#0B702A] text-white font-semibold rounded-lg hover:bg-[#0C9F39] transition-all duration-300 shadow-lg hover:scale-105"
                >
                  Sites &amp; Landing Pages
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Service 3 - Desenvolvimento Seguro */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center lg:grid-flow-dense">
              <div className="lg:col-start-2 relative h-96 rounded-xl overflow-hidden">
                <img src="https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=600" alt="Desenvolvimento Seguro" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-8 left-8">
                  <div className="w-16 h-16 bg-[#0B702A]/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
                    <Code className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>

              <div className="lg:col-start-1 lg:row-start-1">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">{t.services.development.title}</h2>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">{t.services.development.description}</p>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-4 text-[#0B702A]">{t.services.whatIncludes}</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.development.features[0]}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.development.features[1]}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.development.features[2]}</span>
                    </li>
                  </ul>
                </div>

                <Link to="/secure-development" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0B702A] text-white font-semibold rounded-lg hover:bg-[#0C9F39] transition-all duration-300 shadow-lg hover:scale-105">
                  {t.services.learnMore}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>

            {/* Service 4 - Auditoria */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative h-96 rounded-xl overflow-hidden">
                <img src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600" alt="Auditoria" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-8 left-8">
                  <div className="w-16 h-16 bg-[#0B702A]/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
                    <Search className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">{t.services.audit.title}</h2>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">{t.services.audit.description}</p>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-4 text-[#0B702A]">{t.services.whatIncludes}</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.audit.features[0]}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.audit.features[1]}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.audit.features[2]}</span>
                    </li>
                  </ul>
                </div>

                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0B702A] text-white font-semibold rounded-lg hover:bg-[#0C9F39] transition-all duration-300 shadow-lg hover:scale-105">
                  {t.services.requestQuote}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>

            {/* Service 5 - Consultoria */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center lg:grid-flow-dense">
              <div className="lg:col-start-2 relative h-96 rounded-xl overflow-hidden">
                <img src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=600" alt="Consultoria" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-8 left-8">
                  <div className="w-16 h-16 bg-[#0B702A]/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
                    <Lock className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>

              <div className="lg:col-start-1 lg:row-start-1">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">{t.services.consulting.title}</h2>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">{t.services.consulting.description}</p>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-4 text-[#0B702A]">{t.services.whatIncludes}</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.consulting.features[0]}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.consulting.features[1]}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#0B702A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-lg">{t.services.consulting.features[2]}</span>
                    </li>
                  </ul>
                </div>

                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0B702A] text-white font-semibold rounded-lg hover:bg-[#0C9F39] transition-all duration-300 shadow-lg hover:scale-105">
                  {t.services.talkSpecialist}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;